import clsx from 'clsx';
import type { ComponentPropsWithRef } from 'react';

import { isExternalURL } from '@commons/helpers/url';
import { Link } from '@commons/i18n/navigation';

import { type ButtonProps, getButtonDefaultIcon } from '.';
import Icon from '../Icon';
import styles from './index.module.scss';

// Type definition of the Button props extended with the HTML anchor element props.

export type ButtonLinkProps = ButtonProps & ComponentPropsWithRef<typeof Link>;

/**
 * ButtonLink component.
 */

export const ButtonLink = (props: ButtonLinkProps) => {
  const {
    href,
    children,
    variant,
    activeColor,
    xl,
    active,
    target,
    disabled,
    unstyled,
    className,
    style,
    ref,
    ...rest
  } = props;

  const isExternal = isExternalURL(href);

  const classes = [
    styles.button,
    styles[`variant-${variant}`],
    styles[`active-${activeColor}`],
    xl && styles.xl,
    active && styles.active,
    disabled && styles.disabled,
    unstyled && styles.unstyled,
    className,
  ];

  // If the button variant supports an icon, conditionally render it.
  let renderedIcon = null;

  if (props.variant !== 'secondary') {
    const icon = props.icon || getButtonDefaultIcon(variant);

    if (icon !== 'none') {
      classes.push(styles[`icon-${icon}`]);
      renderedIcon = <Icon type={icon} className={styles.icon} />;
    }
  }

  // Remove the icon property from `rest` if present to avoid spreading it.
  if ('icon' in rest) delete rest.icon;

  return (
    <Link
      ref={ref}
      href={href || '#'}
      target={target || (isExternal ? '_blank' : undefined)}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      className={clsx(classes)}
      style={style}
      {...rest}
    >
      {renderedIcon}
      <span className={styles.content}>{children}</span>
    </Link>
  );
};

export default ButtonLink;
